import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CTA from "@/components/CTA";
import { 
  Phone, 
  Calendar, 
  ArrowRightLeft, 
  Clock, 
  FileText, 
  Globe, 
  Settings,
  MessageSquare,
  Shield,
  Zap
} from "lucide-react";

const features = [
  {
    icon: Phone,
    title: "AI Voice Assistant",
    description: "Natural, human-like conversations powered by advanced AI. Your callers won't know it's not a real person. CallChat understands context, handles interruptions, and adapts to each caller.",
    highlights: ["Natural language understanding", "Human-like voice", "Context awareness"],
  },
  {
    icon: Calendar,
    title: "Appointment Scheduling",
    description: "Automatically book appointments directly into your calendar. CallChat checks availability, suggests times, and sends confirmation emails—all without human intervention.",
    highlights: ["Calendar integration", "Real-time availability", "Automatic confirmations"],
  },
  {
    icon: ArrowRightLeft,
    title: "Smart Call Routing",
    description: "Not every call needs AI. CallChat intelligently routes urgent or complex calls to your team while handling routine inquiries automatically.",
    highlights: ["Priority detection", "Team availability check", "Seamless handoff"],
  },
  {
    icon: Clock,
    title: "Business Hours & Holidays",
    description: "Configure your availability with precision. Set different greetings for after-hours, holidays, or special events. CallChat always knows what to say.",
    highlights: ["Custom schedules", "Holiday modes", "After-hours handling"],
  },
  {
    icon: FileText,
    title: "Call Summaries & Logs",
    description: "Every call is transcribed and summarized. Review conversations, track outcomes, and identify trends—all from your dashboard.",
    highlights: ["Full transcriptions", "AI summaries", "Searchable logs"],
  },
  {
    icon: Globe,
    title: "Multilingual Conversations",
    description: "Serve customers in Dutch, French, and English. CallChat automatically detects the caller's language and responds fluently.",
    highlights: ["Dutch (NL)", "French (FR)", "English (EN)"],
  },
  {
    icon: Settings,
    title: "Easy Setup Dashboard",
    description: "Get started in minutes with our intuitive dashboard. Train CallChat on your business, customize responses, and monitor performance.",
    highlights: ["No coding required", "Quick onboarding", "Real-time analytics"],
  },
  {
    icon: MessageSquare,
    title: "Custom Greetings & Scripts",
    description: "Make CallChat sound like your brand. Create custom greetings, define how to handle specific questions, and set the tone you want.",
    highlights: ["Brand voice", "Custom FAQs", "Scripted responses"],
  },
];

const Features = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Powerful Features for <span className="text-gradient">Modern Businesses</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Everything you need to automate your phone support and never miss a customer call again.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-card transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      {feature.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {feature.highlights.map((highlight, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8 lg:gap-12">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                <Shield className="w-12 h-12 text-primary-foreground" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                  Enterprise-Grade Security & GDPR Compliance
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Your data is protected with bank-level encryption. CallChat is fully GDPR compliant and hosted on European servers. We never share your data with third parties.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Zap className="w-4 h-4 text-primary" />
                    256-bit Encryption
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Zap className="w-4 h-4 text-primary" />
                    EU Data Centers
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Zap className="w-4 h-4 text-primary" />
                    GDPR Compliant
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
};

export default Features;
